<template>
  <div
    class="lottie-adapter"
    :style="wrapperStyle"
  >
    <div
      ref="container"
      class="lottie-adapter__container"
      :style="style"
    />
    <div
      v-if="showPreloader"
      class="lottie-adapter__loader"
      :class="{ 'lottie-adapter__loader--loaded': loaded }"
    >
      <div class="lottie-adapter__spinner" />
    </div>
  </div>
</template>

<script>
import lottie from 'lottie-web';

export default {
  props: {
    path: { type: String },
    animationData: { type: Object },
    autoplay: { type: Boolean, default: true },
    loop: { type: Boolean, default: true },
    height: Number,
    width: Number,
    showPreloader: { type: Boolean, default: false },
  },
  emits: [
    'animCreated',
    'loaded',
  ],
  data() {
    return {
      loaded: false,
      wrapperStyle: {
        width: this.width ? `${this.width}px` : '100%',
        height: this.height ? `${this.height}px` : '100%',
        overflow: 'hidden',
      },
      style: {
        width: this.width ? `${this.width}px` : '100%',
        height: this.height ? `${this.height}px` : '100%',
        overflow: 'hidden',
      },
      config: {
        renderer: 'svg',
        loop: this.loop,
        autoplay: this.autoplay,
      },
    };
  },
  watch: {
    animationData() {
      this.loadAnimation();
    },
    path() {
      this.loaded = false;
      this.loadAnimation();
    },
  },
  mounted() {
    this.loadAnimation();
  },
  beforeUnmount() {
    if (this.anim) {
      this.anim.destroy();
      this.anim = null;
    }
  },
  methods: {
    loadAnimation() {
      if (this.animationData) this.config.animationData = this.animationData;
      else if (this.path) this.config.path = this.path;
      else throw new Error('Either "path" or "animationData" has to be provided');
      this.config.container = this.$refs.container;

      if (this.anim) this.anim.destroy();
      this.anim = lottie.loadAnimation(this.config);
      this.anim.addEventListener('DOMLoaded', () => {
        this.fitSvgToContainer();
        this.loaded = true;
        this.$emit('loaded');
      });
      this.$emit('animCreated', this.anim);
    },
    fitSvgToContainer() {
      const svg = this.$refs.container?.querySelector('svg');
      if (svg) {
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        svg.style.maxWidth = '100%';
        svg.style.maxHeight = '100%';
        svg.style.width = '100%';
        svg.style.height = '100%';
        svg.style.overflow = 'hidden';
      }
    },
  },
};
</script>

<style lang="less" scoped>
.lottie-adapter {
  position: relative;

  &__container {
    overflow: hidden;
    width: 100%;
    height: 100%;

    :deep(svg) {
      display: block;
      width: 100% !important;
      height: 100% !important;
      max-width: 100% !important;
      max-height: 100% !important;
      object-fit: contain;
    }
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fff;
    border-radius: 4px;
    z-index: 2;
    opacity: 1;
    transition: opacity 0.3s ease-out;

    &--loaded {
      opacity: 0;
      pointer-events: none;
    }
  }

  &__spinner {
    width: 24px;
    height: 24px;
    border: 2px solid #e0d5b7;
    border-top: 2px solid #337870;
    border-radius: 50%;
    animation: lottie-adapter-spin 1s linear infinite;
  }
}

@keyframes lottie-adapter-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
